import { useCallback, useEffect, useRef, useState } from "react";
import { intervalFor, nextStep, FASTEST, SLOWEST } from "./pollCadence";

/**
 * Polls something, as often as it is worth polling it.
 *
 * Answers three questions the old fixed interval did not:
 *
 *   when does it start   on mount, but only once there is something to poll
 *                        for — no session, no polling — and the first call is
 *                        immediate rather than one interval late
 *   how often            the cadence ladder: fast while news is arriving,
 *                        stepping down to every 30s while nothing is
 *                        (pollCadence.js)
 *   when does it stop    when the tab is hidden, when the thing being polled
 *                        goes away, and on unmount. Coming back polls at once
 *                        and resets to the top of the ladder, so a teacher
 *                        returning to the tab is never looking at stale chat
 *
 * A poll that overruns its own interval cannot stack up: the next one is
 * scheduled after the last finishes, not on a fixed clock.
 *
 * @param {() => Promise<boolean>} onPoll resolves true when it brought news
 */
export function useAdaptivePoll(
  onPoll,
  { enabled = true, pauseWhenHidden = true, floorOnly = false } = {}
) {
  const [step, setStep] = useState(FASTEST);
  const timer = useRef(null);
  const running = useRef(false);
  const stepRef = useRef(FASTEST);
  const poll = useRef(onPoll);
  poll.current = onPoll;
  const floorOnlyRef = useRef(floorOnly);
  floorOnlyRef.current = floorOnly;

  const clear = () => {
    clearTimeout(timer.current);
    timer.current = null;
  };

  // run and schedule call each other, so the loop is closed through a ref
  // rather than a dependency cycle neither of them can honestly declare.
  const runRef = useRef(null);

  const schedule = useCallback((ms) => {
    clear();
    timer.current = setTimeout(() => runRef.current?.(), ms);
  }, []);

  const run = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    let hadNews = false;
    try {
      hadNews = Boolean(await poll.current());
    } catch {
      // A failed poll is not news, so the ladder keeps stepping down rather
      // than hammering a server that is already unhappy.
      hadNews = false;
    } finally {
      running.current = false;
    }
    // With a live stream doing the work, the poll is only a net for anything
    // the stream missed, so it sits at the slowest rung regardless.
    const next = floorOnlyRef.current ? SLOWEST : nextStep(stepRef.current, hadNews);
    stepRef.current = next;
    setStep(next);
    schedule(intervalFor(next));
  }, [schedule]);

  runRef.current = run;

  /** Something happened worth watching for a reply to. */
  const quicken = useCallback(() => {
    if (floorOnlyRef.current) return; // the stream already covers this
    stepRef.current = FASTEST;
    setStep(FASTEST);
    schedule(intervalFor(FASTEST));
  }, [schedule]);

  /** Poll now, without waiting for the next tick. */
  const pollNow = useCallback(() => {
    stepRef.current = FASTEST;
    setStep(FASTEST);
    run();
  }, [run]);

  useEffect(() => {
    if (!enabled) {
      clear();
      return undefined;
    }

    const hidden = () => pauseWhenHidden && document.visibilityState === "hidden";

    const start = () => {
      stepRef.current = FASTEST;
      setStep(FASTEST);
      run();
    };

    const onVisibility = () => {
      if (hidden()) {
        // Nobody is looking; stop asking.
        clear();
      } else {
        // Back in the room: catch up at once rather than on the old schedule.
        start();
      }
    };

    if (!hidden()) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, pauseWhenHidden, run]);

  return { intervalMs: intervalFor(step), step, quicken, pollNow };
}
